import axios from 'axios';
import path from 'path';

import Spec from './Spec';

export interface StudentSubmissionType {
  id: string
  userId: string
  courseId: string
  courseWorkId: string
  draftGrade: number | null
  assignedGrade: number | null
  alternateLink: string
  assignmentSubmission: AssignmentSubmission
  state: string
  courseWorkType: string
  associatedWithDeveloper: boolean
  creationTime: string
  updateTime: string
}

export interface AssignmentSubmission {
  attachments: DriveFile[]
}

export interface DriveFile {
  alternateLink: string
  id: string
  thumbnailUrl: string
  title: string
}

export interface MoodleFile {
  alternateLink: string;
  id: string;
  title: string;
}

class StudentSubmission implements StudentSubmissionType {
  id: string
  userId: string
  courseId: string
  courseWorkId: string
  draftGrade: number | null
  assignedGrade: number | null
  alternateLink: string
  assignmentSubmission: AssignmentSubmission
  state: string
  courseWorkType: string
  associatedWithDeveloper: boolean
  creationTime: string
  updateTime: string

  constructor(studentSubmission: StudentSubmissionType) {
    const { draftGrade = null, assignedGrade = null } = studentSubmission;
    this.id = studentSubmission.id;
    this.userId = studentSubmission.userId;
    this.courseId = studentSubmission.courseId;
    this.courseWorkId = studentSubmission.courseWorkId;
    this.draftGrade = draftGrade;
    this.assignedGrade = assignedGrade;
    this.alternateLink = studentSubmission.alternateLink;
    this.assignmentSubmission = studentSubmission.assignmentSubmission;
    this.state = studentSubmission.state;
    this.courseWorkType = studentSubmission.courseWorkType;
    this.associatedWithDeveloper = studentSubmission.associatedWithDeveloper;
    this.creationTime = studentSubmission.creationTime;
    this.updateTime = studentSubmission.updateTime;
  }

  static async find(query?: {courseId: string, courseWorkId: string}): Promise<StudentSubmission[]> {
    const { data = [] } = await axios.get('/classroom/courses/course-work/student-submissions/list', {
      params: query
    });
    return (data as StudentSubmissionType[]).map(a => new StudentSubmission(a));
  }

  static async findByStudentId(studentId: string, query?: {courseId: string, courseWorkId: string}): Promise<StudentSubmission[]> {
    const data = await this.find(query);
    return (data as StudentSubmissionType[]).filter(a => a.userId === studentId).map(a => new StudentSubmission(a));
  }

  static async submit(courseId: string, courseWorkId: string, file: File, workItemName: string, driveFileId: string, specId: string, courseWorkTitle: string) {
    const formData = new FormData();

      formData.append('courseId', courseId);
      formData.append('courseWorkId', courseWorkId);
      formData.append('file', file);
      formData.append('specId', specId);
      formData.append('workItemName', workItemName);
      formData.append('driveFileId', driveFileId);

      return await axios.post('/classroom/courses/course-work/submit', formData);
  }

  async setAssignedGrade(grade: number | null): Promise<StudentSubmission> {
    await axios.post('/classroom/courses/course-work/student-submission/set-assigned-grade', {
      studentId: this.userId,
      courseId: this.courseId,
      courseWorkId: this.courseWorkId,
      courseWorkSubmissionId: this.id,
      grade: grade
    });
    this.assignedGrade = grade;
    this.draftGrade = grade;
    return this;
  }

  getDriveFiles(): DriveFile[] {
    const driveFiles: DriveFile[] = [];
    if (this.assignmentSubmission && this.assignmentSubmission.attachments) {
      for (let attachment of this.assignmentSubmission.attachments) {
        const driveFile = (attachment as any).driveFile as DriveFile | null;
        if (driveFile) {
          driveFiles.push(driveFile);
        }
      }
    }
    return driveFiles;
  }

  getDriveFileBySpec(spec: Spec): DriveFile|null {
    const driveFiles = this.getDriveFiles();
    for (let driveFile of driveFiles) {
      const extension = path.extname(driveFile.title);
      const name = path.basename(driveFile.title, extension);
      if (name === spec.name) {
        return driveFile;
      }
    }
    return null;
  }

  getGrade(): number | null {
    if (typeof this.assignedGrade === 'number')
      return this.assignedGrade;
    if (typeof this.draftGrade === 'number')
      return this.draftGrade;
    return null;
  }
}

export default StudentSubmission;
